// src/screens/StartHireView.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Container, Center, Flex, Text, Button, Alert } from '@mantine/core';
import {
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
  Elements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { TimerWheelView } from 'react-timer-wheel-kit';
import {
  getSite,
  getCurrentStripePK,
  getCurrentBusinessID,
  // initial hire
  fetchHireableItemPaymentIntent,
  createHiredSession,
  // extension
  fetchHireableItemExtendPaymentIntent,
  extendHiredSession,
} from '../atoms/network';

/** Try real hard to find a pk_ key in the payload */
function extractPk(data) {
  if (!data || typeof data !== 'object') return null;
  const tryList = (obj) =>
    [
      obj.primaryKey,
      obj.stripe_pk,
      obj.stripePublishableKey,
      obj.stripe_publishable_key,
      obj.publishable_key,
      obj.publishableKey,
      obj.pk,
      obj.key,
    ].filter(Boolean);

  const flat = tryList(data);
  const biz = data.business || data.biz || data.company || {};
  const nestedBiz = tryList(biz);
  const item = data.item || {};
  const nestedItem = tryList(item);

  const candidate = [...flat, ...nestedBiz, ...nestedItem].find(
    (v) => typeof v === 'string' && /^pk_(test|live)_/.test(v)
  );
  return candidate || null;
}

// ---------- Outer wrapper: loads business pk & mounts <Elements>
export default function StartHireView(props) {
  const { itemID } = props;
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let pk = getCurrentStripePK?.() || null;
        if (!pk) {
          const res = await getSite(itemID);
          pk = extractPk(res?.data) || extractPk(res);
        }
        if (!pk || !/^pk_(test|live)_/.test(pk)) {
          throw new Error('Stripe publishable key not provided for this business.');
        }
        if (alive) setStripePromise(loadStripe(pk));
      } catch (e) {
        if (alive) setBootError(e?.message || 'Failed to initialize payments.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [itemID]);

  if (bootError) {
    return (
      <Container size={700} px="md" style={{ minHeight: '40vh' }}>
        <Center style={{ paddingTop: 24 }}>
          <Text c="red">{bootError}</Text>
        </Center>
      </Container>
    );
  }

  if (loading || !stripePromise) return null;

  return (
    <Elements stripe={stripePromise}>
      <StartHireViewInner {...props} />
    </Elements>
  );
}

// ---------- Inner component (calls useStripe/useElements) ----------
const cardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: '18px',
      lineHeight: '24px',
      '::placeholder': { color: '#a0aec0' },
    },
    invalid: { color: '#fa755a' },
  },
};

function StartHireViewInner({
  itemID,
  userPhoneNumber,
  maxMinutes,
  costPerMinute,
  isExtension = false,
  sessionId = null,
  alreadyPurchasedMinutes = 0,
  onSessionReady,
  onExtended,
  onBack,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [walletAvailable, setWalletAvailable] = useState(false); // Apple/Google Pay only
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletType, setWalletType] = useState(null); // 'apple' | 'google' | null

  const [showCard, setShowCard] = useState(false);
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState('');
  const [hint, setHint] = useState('');
  const [hasSelection, setHasSelection] = useState(false); // 👈 only show wallet button after spin

  // values captured when the wheel completes
  const selectedMinutesRef = useRef(0);
  const totalCentsRef = useRef(0);
  const wheelDoneRef = useRef(null);

  const titleText = isExtension ? 'Extend your hire' : 'Start your hire';
  const subtitleText = isExtension
    ? 'Spin the wheel below to choose how long to extend'
    : 'Spin the wheel below to select the length of your hire';
  const paySheetLabel = (m) => (isExtension ? `Extend for ${m} min` : `Hire for ${m} min`);

  // Shared handlers for PaymentRequest events (stable identities)
  const handlePRCancel = useCallback(() => {
    wheelDoneRef.current?.(false);
  }, []);

  const handlePRPaymentMethod = useCallback(
    async (ev) => {
      try {
        const businessID = getCurrentBusinessID();

        // Get the correct intent based on mode
        let clientSecret, transactionId;
        if (isExtension) {
          const resp = await fetchHireableItemExtendPaymentIntent({
            itemID,
            sessionId,
            minutes: selectedMinutesRef.current,
            totalCents: totalCentsRef.current,
            businessID,
          });
          clientSecret = resp.clientSecret;
          transactionId = resp.transactionId;
        } else {
          const resp = await fetchHireableItemPaymentIntent({
            itemID,
            phone: userPhoneNumber,
            minutes: selectedMinutesRef.current,
            totalCents: totalCentsRef.current,
            businessID,
          });
          clientSecret = resp.clientSecret;
          transactionId = resp.transactionId;
        }

        const { error: confErr, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confErr) {
          ev.complete('fail');
          wheelDoneRef.current?.(false);
          setErr(confErr.message || 'Payment failed.');
          return;
        }

        ev.complete('success');

        if (paymentIntent && paymentIntent.status === 'requires_action') {
          const { error: actionErr } = await stripe.confirmCardPayment(clientSecret);
          if (actionErr) {
            wheelDoneRef.current?.(false);
            setErr(actionErr.message || 'Payment authorization failed.');
            return;
          }
        }

        // After successful payment:
        if (isExtension) {
          const { newTotalMinutes, remainingSeconds } = await extendHiredSession({
            sessionId,
            minutes: selectedMinutesRef.current,
            transactionId,
            businessID,
          });
          wheelDoneRef.current?.(true);
          onExtended?.({ newTotalMinutes, remainingSeconds });
        } else {
          const { sessionId: sid, length } = await createHiredSession({
            transactionId,
            minutes: selectedMinutesRef.current,
            itemID,
            phone: userPhoneNumber,
            businessID,
          });
          wheelDoneRef.current?.(true);
          onSessionReady?.({ sessionId: sid, minutes: selectedMinutesRef.current, length });
        }
      } catch (e) {
        try { ev.complete('fail'); } catch {}
        wheelDoneRef.current?.(false);
        setErr(e?.message || 'Payment error.');
      }
    },
    [stripe, isExtension, itemID, sessionId, userPhoneNumber, onExtended, onSessionReady]
  );

  // Build & cache Payment Request once (HTTPS only). Reuse it later with pr.update()
  useEffect(() => {
    if (!stripe) return;
    if (!(window.isSecureContext && window.location.protocol === 'https:')) return;

    const pr = stripe.paymentRequest({
      country: 'AU',
      currency: 'aud',
      total: { label: 'Hire', amount: 100 }, // placeholder; we update before user taps
      requestPayerName: false,
      requestPayerEmail: false,
      requestShipping: false,
    });

    let mounted = true;

    pr.canMakePayment().then((can) => {
      if (!mounted) return;
      const apple = !!can?.applePay;
      const google = !!can?.googlePay;
      if (!apple && !google) {
        setWalletAvailable(false);
        setPaymentRequest(null);
        setWalletType(null);
        return;
      }
      setWalletType(apple ? 'apple' : 'google');
      setWalletAvailable(true);
      setPaymentRequest(pr);

      pr.on('paymentmethod', handlePRPaymentMethod);
      pr.on('cancel', handlePRCancel);
    });

    return () => {
      mounted = false;
      try { pr.off?.('paymentmethod', handlePRPaymentMethod); } catch {}
      try { pr.off?.('cancel', handlePRCancel); } catch {}
    };
  }, [stripe, handlePRPaymentMethod, handlePRCancel]);

  // When wheel asks to pay, prep the PR and show UI
  const handleRequestPayment = async (minutes, cost, done) => {
    setErr('');
    setHint('');
    setShowCard(false);

    wheelDoneRef.current = done;
    selectedMinutesRef.current = minutes;
    totalCentsRef.current = Math.round((Number(cost) || 0) * 100);

    if (paymentRequest && walletAvailable) {
      paymentRequest.update({
        total: { label: paySheetLabel(minutes), amount: totalCentsRef.current },
      });
      setHasSelection(true); // 👈 show wallet button now
      setHint(`Tap the ${walletType === 'apple' ? 'Apple Pay' : 'Google Pay'} button below to complete payment.`);
      return;
    }

    // No wallet support → card fallback
    setHasSelection(true);
    setShowCard(true);
    done(false);
  };

  const payWithCard = async () => {
    setErr('');
    setHint('');
    setPaying(true);
    try {
      const businessID = getCurrentBusinessID();

      // 1) Get client_secret (mode-dependent)
      let clientSecret, transactionId;
      if (isExtension) {
        const resp = await fetchHireableItemExtendPaymentIntent({
          itemID,
          sessionId,
          minutes: selectedMinutesRef.current,
          totalCents: totalCentsRef.current,
          businessID,
        });
        clientSecret = resp.clientSecret;
        transactionId = resp.transactionId;
      } else {
        const resp = await fetchHireableItemPaymentIntent({
          itemID,
          phone: userPhoneNumber,
          minutes: selectedMinutesRef.current,
          totalCents: totalCentsRef.current,
          businessID,
        });
        clientSecret = resp.clientSecret;
        transactionId = resp.transactionId;
      }

      // 2) Confirm card payment
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Card input not ready');

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card },
      });
      if (error) throw error;

      if (paymentIntent && paymentIntent.status === 'requires_action') {
        const { error: actionErr } = await stripe.confirmCardPayment(clientSecret);
        if (actionErr) throw actionErr;
      }

      // 3) Finalize
      if (isExtension) {
        const { newTotalMinutes, remainingSeconds } = await extendHiredSession({
          sessionId,
          minutes: selectedMinutesRef.current,
          transactionId,
          businessID,
        });
        wheelDoneRef.current?.(true);
        onExtended?.({ newTotalMinutes, remainingSeconds });
      } else {
        const { sessionId: sid, length } = await createHiredSession({
          transactionId,
          minutes: selectedMinutesRef.current,
          itemID,
          phone: userPhoneNumber,
          businessID,
        });
        wheelDoneRef.current?.(true);
        onSessionReady?.({ sessionId: sid, minutes: selectedMinutesRef.current, length });
      }

      setShowCard(false);
    } catch (e) {
      setErr(e?.message || 'Card payment failed.');
      wheelDoneRef.current?.(false);
    } finally {
      setPaying(false);
    }
  };

  return (
    <Container key="hire-select" size={700} px="md" style={{ minHeight: '100vh' }}>
      <Center style={{ paddingTop: 24, paddingBottom: 100 }}>
        <Flex direction="column" align="center" style={{ width: '100%', maxWidth: 420 }}>
          {isExtension && (
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                setShowCard(false);
                setErr('');
                setHint('');
                setHasSelection(false);
                wheelDoneRef.current?.(false);
                onBack?.();
              }}
              style={{ alignSelf: 'flex-start', marginBottom: 4 }}
            >
              ← Back
            </Button>
          )}

          <Text fw="bold" fz={24} ta="center">{titleText}</Text>
          <Text pb={12} ta="center">{subtitleText}</Text>

          <TimerWheelView
            config={{
              costPerMinute,
              maxMinutes,
              allowsNegative: false,
              snapDegree: 30,
              ringLineWidth: 46,
              ringGradient: { colors: ['rgba(0,122,255,0.35)', 'rgba(0,122,255,0.85)'] },
              shouldPlaySuccessHaptic: true,
            }}
            onRequestPayment={handleRequestPayment}
          />

          {/* Shared row for wallet button + hint so widths always match */}
          {hasSelection && (
            <div style={{ width: '100%', marginTop: 16 }}>
              {walletAvailable && paymentRequest && (
                <div style={{ width: '100%', marginBottom: hint ? 8 : 0 }}>
                  <PaymentRequestButtonElement
                    options={{
                      paymentRequest,
                      style: {
                        paymentRequestButton: {
                          type: 'buy',
                          theme: 'dark',
                          height: '48px',
                        },
                      },
                    }}
                  />
                </div>
              )}

              {hint && (
                <Alert mt={0} variant="light" style={{ width: '100%' }}>
                  {hint}
                </Alert>
              )}
            </div>
          )}

          {/* Card fallback */}
          {hasSelection && showCard && (
            <div style={{ width: '100%', marginTop: 16 }}>
              <CardElement options={cardElementOptions} />
              <Button fullWidth mt="md" loading={paying} onClick={payWithCard}>
                {isExtension ? 'Pay & Extend' : 'Pay & Continue'}
              </Button>
            </div>
          )}

          {err && <Text mt="sm" c="red">{err}</Text>}
        </Flex>
      </Center>
    </Container>
  );
}
